import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api, type BrandingLimits, type BrandingSettings } from './api.ts';
import './branding.css';

/**
 * White-label branding for generated reports.
 *
 * The report is the artefact the buyer's customer actually sees, and an MSP will
 * not hand a client a document carrying somebody else's name — ADR-0020 puts
 * this in Phase 2 as table stakes rather than polish. The branding is
 * deployment-level: one MSP behind one deployment, so there is nothing to scope
 * it to and no tenant in any of these calls.
 *
 * This is a panel, not a page: it renders inside Settings, which owns the h1.
 *
 * **This screen validates nothing.** Every branding rule — the hex format, the
 * accepted image types, the size cap, the length limits — has exactly one
 * implementation, in `@driftwatch/contracts` on the server. This project has
 * twice shipped two implementations of one rule that disagreed, and both times
 * the disagreement was invisible until it mattered. So there is no hex regex
 * here, no MIME allow-list and no length rule: the console posts what it has and
 * renders what the server says, and every number and list it uses comes from the
 * `limits` object the server itself returned.
 *
 * The single exception is not validation but a message: a file larger than the
 * server's own cap is rejected before it is read, because base64 expansion would
 * push the request past Fastify's 1 MiB body limit and the user would get a bare
 * 413 with nothing to act on.
 */

const EMPTY: BrandingSettings = {
  organisationName: null,
  logoDataUri: null,
  brandColour: null,
  footerText: null,
};

/**
 * Where the colour picker starts when no brand colour is set. It is the widget's
 * initial position and nothing more — it is never sent unless the MSP clears the
 * "no brand colour" box, which is what makes choosing a colour deliberate.
 */
const PICKER_START = '#23408e';

const message = (cause: unknown): string =>
  cause instanceof Error ? cause.message : String(cause);

const kb = (bytes: number): string => `${Math.round(bytes / 1024)} kB`;

const FIELD_LABELS: Record<string, string> = {
  organisationName: 'Organisation name',
  logoDataUri: 'Logo',
  brandColour: 'Brand colour',
  footerText: 'Footer line',
};

/**
 * Renders the server's own rejection rather than pre-empting it. A 400 carries a
 * Zod `flatten()`, whose field names are the wire names; only the labels are
 * ours, so the sentence reads as English without the console deciding anything.
 */
const serverProblems = (cause: unknown): string[] | null => {
  if (!(cause instanceof ApiError) || cause.status !== 400) return null;

  const body = cause.body;
  if (typeof body !== 'object' || body === null) return null;

  const detail = (body as { error?: unknown }).error;
  if (typeof detail !== 'object' || detail === null) return null;

  const { formErrors, fieldErrors } = detail as { formErrors?: unknown; fieldErrors?: unknown };
  const problems: string[] = [];

  if (Array.isArray(formErrors)) problems.push(...formErrors.map(String));
  if (typeof fieldErrors === 'object' && fieldErrors !== null) {
    for (const [field, reasons] of Object.entries(fieldErrors as Record<string, unknown>)) {
      if (!Array.isArray(reasons)) continue;
      problems.push(...reasons.map((reason) => `${FIELD_LABELS[field] ?? field}: ${String(reason)}`));
    }
  }

  return problems.length > 0 ? problems : null;
};

export const Branding = () => {
  const [draft, setDraft] = useState<BrandingSettings>(EMPTY);
  const [limits, setLimits] = useState<BrandingLimits | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  /**
   * A screen reader does not re-announce a live region whose text is unchanged,
   * and saving twice in a row is ordinary here. The alternating zero-width space
   * guarantees a change without a visible one.
   */
  const announce = useCallback((text: string) => {
    setAnnouncement((previous) => (previous.endsWith('\u200B') ? text : `${text}\u200B`));
  }, []);

  useEffect(() => {
    api
      .getBranding()
      .then((view) => {
        setDraft(view.branding);
        setLimits(view.limits);
        setUpdatedAt(view.updatedAt);
      })
      .catch((cause: unknown) => setError(message(cause)))
      .finally(() => setLoading(false));
  }, []);

  const patch = (change: Partial<BrandingSettings>) =>
    setDraft((previous) => ({ ...previous, ...change }));

  const readLogo = (file: File | null) => {
    if (!file || !limits) return;
    setStatus(null);

    if (file.size > limits.maxLogoBytes) {
      setError(
        `That file is ${kb(file.size)}. Logos are accepted up to ${kb(limits.maxLogoBytes)} — export a smaller PNG and try again.`,
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setError(null);
      patch({ logoDataUri: String(reader.result) });
      announce('Logo loaded. It is not applied to reports until you save.');
    };
    reader.onerror = () => setError('That file could not be read.');
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    patch({ logoDataUri: null });
    if (fileRef.current) fileRef.current.value = '';
    announce('Logo removed from the form. Save to apply it.');
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const saved = await api.putBranding(draft);
      setDraft(saved.branding);
      setUpdatedAt(saved.updatedAt);
      setStatus('Branding saved. Reports generated from now on will use it.');
      announce('Branding saved. Reports generated from now on will use it.');
    } catch (cause) {
      const problems = serverProblems(cause);
      if (cause instanceof ApiError && cause.status === 413) {
        setError(
          'That logo is too large to send. Export it at a smaller size — a report header rarely needs more than about 400 pixels across.',
        );
      } else if (problems) {
        setError(problems.join(' '));
      } else {
        setError(message(cause));
      }
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await api.clearBranding();
      setDraft(EMPTY);
      setUpdatedAt(null);
      if (fileRef.current) fileRef.current.value = '';
      setStatus('Branding cleared. Reports will use the default styling.');
      announce('Branding cleared. Reports will use the default styling.');
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(false);
    }
  };

  /**
   * The only authoritative check. The in-page preview is an approximation of a
   * band and a footer; the renderer is the thing that decides what a client sees.
   */
  const openSample = async (options?: { print?: boolean }) => {
    setError(null);
    try {
      const { fixtures } = await api.demoFixtures();
      const first = fixtures[0];
      if (!first) {
        setError('No sample tenant is available on this deployment.');
        return;
      }
      await api.openRendered(api.demoReportPath(first.name), options);
    } catch (cause) {
      setError(message(cause));
    }
  };

  const footer = draft.footerText ?? '';
  const colour = draft.brandColour;

  return (
    <section className="branding-panel" aria-labelledby="branding-heading">
      <h2 id="branding-heading">Report branding</h2>
      <p className="lead">
        Your logo, your colour and your footer line on every report you generate. The report is what
        your client reads, and it should carry your name rather than ours.
      </p>

      <p aria-live="polite" role="status" className="sr-only">
        {announcement}
      </p>

      {/*
        The one misreading that costs an MSP an afternoon: assuming the brand
        colour restyles the console they are looking at. Stated first, in the
        heading of the notice, rather than inferred from the preview.
      */}
      <div className="callout branding-scope">
        <span className="callout-title">
          This styles the reports your clients receive — not this console
        </span>
        Nothing here changes the appearance of DriftWatch itself. It changes the document your client
        opens, and it applies to the whole deployment rather than to individual client tenants.
      </div>

      {error && <div className="error">{error}</div>}

      {loading || !limits ? (
        <p className="note">Loading…</p>
      ) : (
        <div className="branding-grid">
          <div className="card branding-form">
            <div className="card-head">
              <h3>What appears on the report</h3>
            </div>
            <div className="card-body">
              <div className="field">
                <label htmlFor="organisation-name">Organisation name</label>
                <input
                  id="organisation-name"
                  value={draft.organisationName ?? ''}
                  maxLength={limits.maxOrganisationNameLength}
                  placeholder="Northwind IT Services"
                  onChange={(event) => patch({ organisationName: event.target.value || null })}
                />
                <span className="hint">Printed beside the logo at the head of every report.</span>
              </div>

              <div className="field">
                <label htmlFor="logo">Logo</label>
                <input
                  id="logo"
                  ref={fileRef}
                  type="file"
                  accept={limits.allowedLogoMimeTypes.join(',')}
                  aria-describedby="logo-help"
                  onChange={(event) => readLogo(event.target.files?.[0] ?? null)}
                />
                {draft.logoDataUri && (
                  <div className="branding-thumb">
                    <img src={draft.logoDataUri} alt="The logo currently in this form" />
                    <button className="btn btn-quiet btn-sm" onClick={removeLogo}>
                      Remove logo
                    </button>
                  </div>
                )}
                <span className="hint" id="logo-help">
                  Up to {kb(limits.maxLogoBytes)}. SVG is not accepted: the report is a single file
                  that gets emailed, printed and opened in viewers we cannot predict, and SVG is the
                  only image format that carries script and external references — export a PNG.
                </span>
              </div>

              <div className="field">
                <label htmlFor="brand-colour">Brand colour</label>
                <div className="branding-colour">
                  <input
                    id="brand-colour"
                    type="color"
                    value={colour ?? PICKER_START}
                    disabled={colour === null}
                    aria-describedby="colour-help"
                    onChange={(event) => patch({ brandColour: event.target.value })}
                  />
                  {/* The hex in words beside the well: the chosen colour is never
                      conveyed by the colour itself. */}
                  <span className="mono branding-swatch-value">
                    {colour ?? 'No brand colour — reports use the default styling'}
                  </span>
                  <span className="branding-check">
                    <input
                      id="no-colour"
                      type="checkbox"
                      checked={colour === null}
                      onChange={(event) =>
                        patch({ brandColour: event.target.checked ? null : PICKER_START })
                      }
                    />
                    <label htmlFor="no-colour">No brand colour</label>
                  </span>
                </div>
                <span className="hint" id="colour-help">
                  The report’s header band uses your exact colour. Link colour is contrast-corrected
                  from it so that it stays legible on both light and dark backgrounds, so links will
                  not match the swatch precisely.
                </span>
              </div>

              <div className="field">
                <label htmlFor="footer-text">Footer line</label>
                <input
                  id="footer-text"
                  value={footer}
                  maxLength={limits.maxFooterTextLength}
                  placeholder="Prepared by Northwind IT Services · hello@northwind.example"
                  aria-describedby="footer-count"
                  onChange={(event) => patch({ footerText: event.target.value || null })}
                />
                <span className="hint" id="footer-count">
                  <span className="num">{footer.length}</span> of{' '}
                  <span className="num">{limits.maxFooterTextLength}</span> characters. Appears at the
                  foot of every page of the report.
                </span>
              </div>

              <div className="callout branding-historical">
                <span className="callout-title">
                  Branding applies to reports generated from now on
                </span>
                Reports are stored as rendered HTML. A report already sent to a client is a
                historical artefact and is not restyled.
              </div>

              <div className="toolbar branding-actions">
                <button
                  className="btn btn-primary"
                  aria-busy={busy || undefined}
                  disabled={busy}
                  onClick={() => void save()}
                >
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button className="btn btn-quiet" disabled={busy} onClick={() => void clear()}>
                  Clear all branding
                </button>
              </div>

              {status && <p className="status-inline">{status}</p>}
              {updatedAt && (
                <p className="note branding-updated">
                  Last changed {new Date(updatedAt).toLocaleString('en-GB')}
                </p>
              )}
            </div>
          </div>

          <div className="branding-side">
            <h3>How the report will look</h3>
            <p className="note branding-side-note">
              A sketch of the band, the head and the footer — not the report itself. Open a sample
              report to see exactly what a client receives; that is the authoritative check.
            </p>

            {/* Drawn as a sheet of paper rather than as a panel, because the
                thing being configured is a document and the reader has to know
                at a glance that they are not previewing this console. */}
            <figure className="doc-preview">
              <div className="doc-page">
                <div
                  className="doc-band"
                  style={colour ? { background: colour } : undefined}
                  aria-hidden="true"
                />
                <div className="doc-head">
                  {draft.logoDataUri ? (
                    <img className="doc-logo" src={draft.logoDataUri} alt="" />
                  ) : (
                    <span className="doc-logo doc-logo-empty">No logo</span>
                  )}
                  <div>
                    <div className="doc-org">{draft.organisationName || 'Your organisation'}</div>
                    <div className="doc-sub">Access control assessment — a client tenant</div>
                  </div>
                </div>
                <div className="doc-body">
                  <p>
                    The findings, their evidence and the remediation steps are printed here in the
                    generated report.
                  </p>
                </div>
                <div className="doc-foot">
                  {footer || 'Your footer line appears at the foot of every page'}
                </div>
              </div>
              <figcaption className="note">
                Approximate. The rendered report is the only authoritative check.
              </figcaption>
            </figure>

            <div className="toolbar branding-sample">
              <button className="btn btn-quiet" onClick={() => void openSample()}>
                Open a sample report
              </button>
              <button className="btn btn-quiet" onClick={() => void openSample({ print: true })}>
                Save a sample as PDF
              </button>
            </div>
            <p className="note">
              Saving as PDF opens the sample report and your browser’s print dialogue. Choose “Save
              as PDF” as the destination to produce the file — the printed document is the same HTML,
              so it carries your branding exactly as a client would see it.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};
